import { memo, useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { useIsMinDesktopSize, useIsMinDesktopSizeL } from "~/common/hooks/useMediaQuery";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { Accordion, AccordionSummary, Typography } from "@schaeffler-commerce/design-system";
import { PCAccordionDetails, PCTableContainer } from "./ComparisionTableStyles";

interface CharacteristicsTableProps {
  products: ProductDescriptor[];
  placeholders: number[];
  showOnlyDifference: boolean;
}

interface RowData {
  key: string;
  label: string;
}

const CharacteristicsTable = memo(({ products, placeholders, showOnlyDifference }: CharacteristicsTableProps) => {
  const { t } = useTranslation();
  const isDesktopL = useIsMinDesktopSizeL();
  const isDesktop = useIsMinDesktopSize();

  const rows = useMemo<RowData[]>(() => {
    const uniqueCategories = new Map<string, RowData>();

    products.forEach((product) => {
      product?.base?.specificationBand?.forEach((item) => {
        const key = item.parentCategoryDescription?.trim();

        if (key && !uniqueCategories.has(key)) {
          uniqueCategories.set(key, {
            key,
            label: key,
          });
        }
      });
    });

    const allRows = Array.from(uniqueCategories.values());

    if (!showOnlyDifference) {
      return allRows;
    }

    return allRows.filter((row) => {
      const values = products.map((product) => {
        const specification = product?.base?.specificationBand?.find(
          (item) => item.parentCategoryDescription?.trim() === row.key,
        );

        return [specification?.name, specification?.description].filter(Boolean).join(";").trim().toLowerCase();
      });

      const uniqueValues = new Set(values);

      return uniqueValues.size > 1;
    });
  }, [products, showOnlyDifference]);

  const columns = useMemo<ColumnDef<RowData>[]>(() => {
    const labelColumn: ColumnDef<RowData> = {
      id: "label",
      size: 246,
      cell: ({ row }) => row.original.label,
    };

    const productColumns = [...products.map((_, index) => index), ...placeholders].map((index) => ({
      id: `product-${index}`,
      size: 250,

      cell: ({ row }: { row: { original: RowData } }) => {
        const product = products[index];
        if (!product) {
          return null;
        }
        const specification = product.base.specificationBand?.find(
          (item) => item.parentCategoryDescription === row.original.key,
        );
        return (
          <>
            {specification?.name}
            {specification?.name && specification?.description ? "; " : ""}
            {specification?.description}
            {!isDesktop && (
              <Typography variant="bodySmall" color="textSecondary">
                {row.original.label}
              </Typography>
            )}
          </>
        );
      },
    }));

    const allColumns: ColumnDef<RowData>[] = [];

    if (isDesktop) {
      allColumns.push(labelColumn);
    }

    allColumns.push(...productColumns);

    if (isDesktopL && allColumns.length) {
      const lastIndex = allColumns.length - 1;

      allColumns[lastIndex] = {
        ...allColumns[lastIndex],
        size: undefined,
      };
    }

    return allColumns;
  }, [products, placeholders, isDesktop, isDesktopL]);

  return (
    <Accordion defaultExpanded>
      <AccordionSummary>
        <Typography component="span">{t("characteristicsTable.tableName")}</Typography>
      </AccordionSummary>
      <PCAccordionDetails>
        <PCTableContainer>
          <TanStackTable
            key={products.length}
            data={rows}
            columns={columns}
            variant="productComparison"
            state={{
              columnPinning: {
                left: ["label"],
              },
            }}
          />
        </PCTableContainer>
      </PCAccordionDetails>
    </Accordion>
  );
});

export default CharacteristicsTable;
